import React, { useState, useEffect } from "react";
import { useFormik, Formik, Field, ErrorMessage, Form } from "formik";
import Select from "react-select";
import {
  threeDotsIcon,
  ViewDetailsIcon,
  EditProgramIcon,
  ManageCodesIcon,
  PauseProgramIcon,
  DeleteProgramIcon,
  BlackAddIcon,
} from "../../assets/icons";
import PaginationTab from "../../components/PaginationTab";
import Table from "../../components/Tables";
import PopUpModal from "../../modals/PopUpModal";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToGetProgramList,
  reqToCreateDiscountProgram,
  reqToUpdateProgram,
  reqToUpdateProgramStatus,
  reqToGetProgramById,
  reqToGetDiscountVariables,
} from "../../redux/slices.js/discountmanagement/discountPromotionSlice";
import * as Yup from "yup";

const ProgramsTab = ({
  tenants,
  openRow,
  setOpenRow,
  dropdownRef,
  currentPage,
  pageSize,
  setCurrentPage,
  setPageSize,
  loading = false,
}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [selectedProgramData, setSelectedProgramData] = useState(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [createProgramLoader, setCreateProgramLoader] = useState(false);

  const totalCount = useSelector(
    (state) => state?.discountCoupons?.totalCounts
  );

  // Calculate totalPages
  const totalPages = Math.ceil(totalCount / pageSize);

  const programVariables = useSelector(
    (state) => state?.discountCoupons?.discount?.data
  );
  const programDetails = useSelector(
    (state) => state?.discountCoupons?.programDetails
  );

  const programs = useSelector(
    (state) => state?.discountCoupons?.programs?.result
  );

  const { discount_types, duration_options, discount_eligibility_rules } =
    programVariables || {};

  useEffect(() => {
    dispatch(
      reqToGetDiscountVariables({
        data: { action: "program" },
        onSuccess: () => {},
        onFailure: (error) => {
          console.error("Error fetching discount variables:", error);
        },
      })
    );
  }, [dispatch]);

  const validationSchema = Yup.object({
    programName: Yup.string().required("Program name is required"),
    discountValue: Yup.number()
      .required("Discount value is required")
      .min(1, "Discount value must be at least 1"),
    discountType: Yup.string().required("Discount type is required"),
    duration: Yup.string().required("Duration is required"),
    maxRedemption: Yup.number()
      .required("Max redemption is required")
      .min(1, "Max redemption must be at least 1"),
    approvalThreshold: Yup.number()
      .required("Approval threshold is required")
      .min(0, "Approval threshold cannot be negative"),
    eligibilityRules: Yup.object().default({}),
  });

  // Options for form fields - USING API DATA
  const discountTypeOptions = discount_types?.map((type) => ({
    value: type.id,
    label: type.name,
  })) || [
    { value: 1, label: "Percentage off" },
    { value: 2, label: "Fixed amount off" },
    { value: 3, label: "Free trial" },
  ];

  const durationOptions = duration_options?.map((option) => ({
    value: option.id,
    label: option.name,
  })) || [
    { value: 1, label: "One-time" },
    { value: 2, label: "Recurring" },
    { value: 3, label: "Forever" },
  ];

  const eligibilityOptions = discount_eligibility_rules?.map((rule) => ({
    id: rule.id,
    label: rule.name,
  })) || [
    { id: "new_customers", label: "New Customers" },
    { id: "existing_customers", label: "Existing Customers" },
    { id: "specific_plan", label: "Specific Plan" },
  ];

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      padding: "4px 8px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#3B82F6",
      },
      backgroundColor: state.isDisabled ? "#F9FAFB" : "#FFFFFF",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3B82F6"
        : state.isFocused
        ? "#EFF6FF"
        : "#FFFFFF",
      color: state.isSelected ? "#FFFFFF" : "#374151",
      padding: "8px 12px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9CA3AF",
    }),
  };

  const fetchProgramDetails = (programId) => {
    setViewDetailsLoading(true);
    dispatch(
      reqToGetProgramById({
        data: { id: programId },
        onSuccess: (response) => {
          const programData = response.result;
          setSelectedProgramData(programData);
          setViewDetailsLoading(false);
        },
        onFailure: (error) => {
          console.error("Error fetching program details:", error);
          setViewDetailsLoading(false);
        },
      })
    );
  };

  const handleViewDetails = (row) => {
    setModalMode("view");
    setSelectedProgramId(row.id);
    setShowModal(true);
    fetchProgramDetails(row.id);
  };

  const handleEditProgram = (row) => {
    setModalMode("edit");
    setSelectedProgramId(row.id);
    setShowModal(true);
    fetchProgramDetails(row.id);
  };

  const handleCreateProgram = () => {
    setModalMode("create");
    setSelectedProgramId(null);
    setSelectedProgramData(null);
    setShowModal(true);
  };

  const handleDeleteProgram = (row) => {
    dispatch(
      reqToUpdateProgramStatus({
        data: { id: row.id, action: "deleted" },
        onSuccess: (response) => {
          // Force refresh the program list
          dispatch(
            reqToGetProgramList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => {
                console.log("Program list refreshed after deletion");
              },
              onFailure: (error) => {
                console.error("Error refreshing program list:", error);
              },
            })
          );
        },
        onFailure: (error) => {
          console.error("Error deleting program:", error);
        },
      })
    );
  };

  const handlePauseProgram = (row) => {
    const action = row.status === "active" ? "deactive" : "active";

    dispatch(
      reqToUpdateProgramStatus({
        data: { id: row.id, action: action },
        onSuccess: (response) => {
          // Force refresh the program list
          dispatch(
            reqToGetProgramList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => {
                console.log("Program list refreshed after status update");
              },
              onFailure: (error) => {
                console.error("Error refreshing program list:", error);
              },
            })
          );
        },
        onFailure: (error) => {
          console.error("Error updating program status:", error);
        },
      })
    );
  };

  const handleCreateProgramSubmit = (values) => {
    setCreateProgramLoader(true);

    const eligibilityRulesArray = Object.keys(values.eligibilityRules)
      .filter((key) => values.eligibilityRules[key] === true)
      .map((key) => parseInt(key));

    const payload = {
      name: values.programName,
      discount_value: Number(values.discountValue),
      discount_type: Number(values.discountType),
      duration: Number(values.duration),
      max_redemptions: Number(values.maxRedemption),
      approval_threshold: Number(values.approvalThreshold),
      eligibility_rules: eligibilityRulesArray,
    };

    dispatch(
      reqToCreateDiscountProgram({
        data: payload,
        onSuccess: (response) => {
          setShowModal(false);
          setCreateProgramLoader(false);
          dispatch(
            reqToGetProgramList({
              data: { page: currentPage, limit: pageSize },
            })
          );
        },
        onFailure: (error) => {
          console.error("Error creating program:", error);
          setCreateProgramLoader(false);
        },
      })
    );
  };

  const handleUpdateProgramSubmit = (values) => {
    setCreateProgramLoader(true);

    const eligibilityRulesArray = Object.keys(values.eligibilityRules)
      .filter((key) => values.eligibilityRules[key] === true)
      .map((key) => parseInt(key));

    const payload = {
      action: "edit",
      name: values.programName,
      discount_value: Number(values.discountValue),
      discount_type: Number(values.discountType),
      duration: Number(values.duration),
      max_redemptions: Number(values.maxRedemption),
      approval_threshold: Number(values.approvalThreshold),
      eligibility_rules: eligibilityRulesArray,
    };

    dispatch(
      reqToUpdateProgram({
        data: payload,
        id: selectedProgramId,
        onSuccess: (response) => {
          setShowModal(false);
          setCreateProgramLoader(false);
          dispatch(
            reqToGetProgramList({
              data: { page: currentPage, limit: pageSize },
            })
          );
        },
        onFailure: (error) => {
          console.error("Error updating program:", error);
          setCreateProgramLoader(false);
        },
      })
    );
  };

  const getInitialValues = () => {
    if (modalMode === "view" || modalMode === "edit") {
      const eligibilityRulesObj = {};
      if (selectedProgramData?.eligibility_rules) {
        selectedProgramData.eligibility_rules.forEach((ruleId) => {
          eligibilityRulesObj[ruleId] = true;
        });
      }

      return {
        programName: selectedProgramData?.name || "",
        discountValue: selectedProgramData?.discount_value || "",
        discountType: selectedProgramData?.discount_type || "",
        duration: selectedProgramData?.duration || "",
        eligibilityRules: eligibilityRulesObj,
        maxRedemption: selectedProgramData?.max_redemptions || "",
        approvalThreshold: selectedProgramData?.approval_threshold || "",
      };
    }

    return {
      programName: "",
      discountValue: "",
      discountType: "",
      duration: "",
      eligibilityRules: {},
      maxRedemption: "",
      approvalThreshold: "",
    };
  };

  const getStatusInfo = (program) => {
    if (program.status === "active") {
      return { text: "Active", className: "bg-[#E9FFDF] text-[#52C41A]" };
    } else if (program.status === "expired" || program.status === "inactive") {
      return { text: "Expired", className: "bg-[#FFEFEE] text-[#EA4335]" };
    } else if (program.status === "deactive") {
      return { text: "Paused", className: "bg-[#FFF4E6] text-[#FA8C16]" };
    } else {
      return { text: "Draft", className: "bg-[#F3F7FF] text-[#4285F4]" };
    }
  };

  const getDiscountTypeLabel = (discountType) => {
    if (discount_types) {
      const discountTypeObj = discount_types.find(
        (type) => type.id === discountType
      );
      return discountTypeObj?.name || "Unknown";
    }

    const typeMap = {
      1: "Percentage off",
      2: "Fixed amount off",
      3: "Free trial",
    };
    return typeMap[discountType] || "Unknown";
  };

  const calculateUsage = (program) => {
    const maxRedemptions = program.max_redemptions || 100;
    const currentUsage = program.current_redemptions || 0;
    const usagePercent = Math.min(
      Math.round((currentUsage / maxRedemptions) * 100),
      100
    );
    return {
      current: currentUsage,
      max: maxRedemptions,
      percent: usagePercent,
    };
  };

  const formatMRRImpact = (program) => {
    const discountValue = parseFloat(program.discount_value) || 0;
    const currentUsage = program.current_redemptions || 0;

    let impact = 0;
    if (program.discount_type === 1) {
      impact = (discountValue / 100) * 100 * currentUsage;
    } else if (program.discount_type === 2) {
      impact = discountValue * currentUsage;
    } else if (program.discount_type === 3) {
      impact = 50 * currentUsage;
    }

    return `$${Math.round(impact).toLocaleString()}`;
  };

  const programColumns = [
    {
      title: "Program",
      key: "program",
      dataIndex: "name",
      render: (value, record) => (
        <>
          {value}
          <div className="mt-1 flex gap-2">
            <span className="rounded-full bg-[#F3F7FF] text-[#4285F4] text-xs font-medium">
              {getDiscountTypeLabel(record.discount_type)}
            </span>
          </div>
        </>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (value, record) => {
        const statusInfo = getStatusInfo(record);
        return (
          <span
            className={`rounded-full text-xs font-medium ${statusInfo.className}`}
          >
            {statusInfo.text}
          </span>
        );
      },
    },
    {
      title: "Usage",
      key: "usage",
      dataIndex: "usage",
      render: (value, record) => {
        const usage = calculateUsage(record);
        return (
          <>
            {usage.current}/{usage.max}
            <div className="flex items-center gap-2 mt-2">
              <div className="w-full h-[5px] bg-gray-100 rounded">
                <div
                  className="h-[5px] bg-[#F45C0C] rounded"
                  style={{ width: `${usage.percent}%` }}
                ></div>
              </div>
              {usage.percent}%
            </div>
          </>
        );
      },
    },
    {
      title: "MRR Impact",
      key: "mrr",
      dataIndex: "mrr_impact",
      render: (value, record) => {
        const mrrImpact = formatMRRImpact(record);
        return (
          <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
            {mrrImpact}
          </span>
        );
      },
    },
    {
      title: "Date Created",
      key: "createdDate",
      dataIndex: "createdAt",
      render: (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return date.toLocaleDateString("en-CA");
      },
    },
    {
      title: "Creator",
      key: "creator",
      dataIndex: "user",
      render: (value) => {
        if (!value) return "-";
        return value.name || "Unknown";
      },
    },
  ];

  const programActions = {
    threeDotsIcon: threeDotsIcon,
    actions: (row) => [
      {
        label: "View details",
        icon: ViewDetailsIcon,
        onClick: () => handleViewDetails(row),
      },
      {
        label: "Edit program",
        icon: EditProgramIcon,
        onClick: () => handleEditProgram(row),
      },
      {
        label: "Manage codes",
        icon: ManageCodesIcon,
        onClick: () => handleEditProgram(row),
      },
      {
        label: row.status === "active" ? "Pause Program" : "Activate Program",
        icon: PauseProgramIcon,
        onClick: () => handlePauseProgram(row),
      },
      {
        label: "Delete Program",
        icon: DeleteProgramIcon,
        onClick: () => handleDeleteProgram(row),
      },
    ],
  };

  const getModalConfig = () => {
    switch (modalMode) {
      case "view":
        return {
          title: "View Program Details",
          confirmText: "Close",
          showCancel: false,
        };
      case "edit":
        return {
          title: "Edit Discount Program",
          confirmText: "Update Program",
          showCancel: true,
        };
      default:
        return {
          title: "Create New Program",
          confirmText: "Create Program",
          showCancel: true,
        };
    }
  };

  const modalConfig = getModalConfig();

  if (!tenants) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading ...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-4 mb-4">
        {/* <button className="btn-light-gray" onClick={handleCreateProgram}>
          {BlackAddIcon} Add New Program
        </button> */}
      </div>

      <div className="grid grid-cols-1 custom-grid-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">Active Programs</p>
          <p className="text-blue-600 text-xl font-bold mb-0">
            {tenants?.dashboard?.activePrograms || 0}
          </p>
        </div>
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">Total Redemptions</p>
          <p className="text-blue-600 text-xl font-bold mb-0">
            {(tenants?.dashboard?.total_redemptions || 0).toLocaleString()}
          </p>
        </div>
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">MRR Impact</p>
          <p className="text-[#EA4335] text-xl font-bold mb-0">
            {(tenants?.dashboard?.mrr_impact || 0).toLocaleString()} saved
          </p>
        </div>
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">Conversion lift</p>
          <p className="text-[#52C41A] text-xl font-bold mb-0">
            {(tenants?.dashboard?.conversion_lift || 0).toFixed(2)}
          </p>
        </div>
      </div>

      <Table
        columns={programColumns}
        data={programs || []}
        rowActions={programActions}
        openRow={openRow}
        setOpenRow={setOpenRow}
        dropdownRef={dropdownRef}
        loading={loading}
        emptyMessage="No programs found"
        keyField="id"
      />

      <PaginationTab
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount} // This now comes from Redux
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />

      <PopUpModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={() => {
          const form = document.querySelector("form");
          if (form) {
            const submitButton = form.querySelector('button[type="submit"]');
            if (submitButton) {
              submitButton.click();
            }
          }
        }}
        title={modalConfig.title}
        confirmText={
          createProgramLoader ? "Processing..." : modalConfig.confirmText
        }
        cancelText={modalConfig.showCancel ? "Close" : undefined}
        confirmDisabled={createProgramLoader}
        loading={createProgramLoader}
        size="large"
        hideConfirm={modalMode === "view"}
      >
        {modalMode === "view" && viewDetailsLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading details...</span>
          </div>
        ) : (
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              if (modalMode === "create") {
                handleCreateProgramSubmit(values);
              } else if (modalMode === "edit") {
                handleUpdateProgramSubmit(values);
              }
            }}
            enableReinitialize
          >
            {({
              values,
              setFieldValue,
              errors,
              touched,
              handleSubmit,
              isValid,
              dirty,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Program Name *
                    </label>
                    <Field
                      type="text"
                      name="programName"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Program name"
                      disabled={modalMode === "view"}
                    />
                    <ErrorMessage
                      name="programName"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Discount Value *
                      </label>
                      <Field
                        type="number"
                        name="discountValue"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="25"
                        disabled={modalMode === "view"}
                      />
                      <ErrorMessage
                        name="discountValue"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Discount Type *
                      </label>
                      <Select
                        options={discountTypeOptions}
                        value={discountTypeOptions.find(
                          (option) => option.value === values.discountType
                        )}
                        onChange={(selectedOption) =>
                          setFieldValue(
                            "discountType",
                            selectedOption?.value || ""
                          )
                        }
                        onBlur={() => {}}
                        styles={customSelectStyles}
                        isSearchable={false}
                        isDisabled={modalMode === "view"}
                      />
                      {touched.discountType && errors.discountType && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors.discountType}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Duration *
                    </label>
                    <Select
                      options={durationOptions}
                      value={durationOptions.find(
                        (option) => option.value === values.duration
                      )}
                      onChange={(selectedOption) =>
                        setFieldValue("duration", selectedOption?.value || "")
                      }
                      onBlur={() => {}}
                      styles={customSelectStyles}
                      isSearchable={false}
                      isDisabled={modalMode === "view"}
                    />
                    {touched.duration && errors.duration && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.duration}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium mb-3">
                      Eligibility Rules *
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {eligibilityOptions.map((option) => (
                        <label
                          key={option.id}
                          className="flex items-center gap-3 cursor-pointer select-none"
                        >
                          <div className="relative !h-5 !w-5">
                            <Field
                              type="checkbox"
                              name={`eligibilityRules.${option.id}`}
                              className="peer hidden !p-0"
                              disabled={modalMode === "view"}
                            />
                            <span className="flex h-5 w-5 items-center justify-center rounded-[6px] border-2 border-[#9BA2AB] peer-checked:border-[#4285F4] bg-white peer-checked:bg-white"></span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="hidden h-4 w-4 text-[#4285F4] peer-checked:block absolute top-[90%] left-[90%] -translate-x-1/2 -translate-y-1/2"
                              fill="#4285F4"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="text-black text-span !leading-[15px] !tracking-[0]">
                            {option.label}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Max Redemption *
                      </label>
                      <Field
                        type="number"
                        name="maxRedemption"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="1000"
                        disabled={modalMode === "view"}
                      />
                      <ErrorMessage
                        name="maxRedemption"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Approval Threshold *
                      </label>
                      <Field
                        type="number"
                        name="approvalThreshold"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="20"
                        disabled={modalMode === "view"}
                      />
                      <ErrorMessage
                        name="approvalThreshold"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>
                </div>

                <button type="submit" className="hidden" />
              </Form>
            )}
          </Formik>
        )}
      </PopUpModal>
    </div>
  );
};

export default ProgramsTab;
